import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { LearningMetricsRead } from '../../types/api';
import { fetchLearningMetrics, type LearningMetricsQuery } from './adminApi';

export interface LearningMetricsResult {
  metrics: LearningMetricsRead | null;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

/** The last completed load, tagged with the query it answers. */
interface Loaded {
  key: string;
  metrics: LearningMetricsRead | null;
  error: string | null;
}

function queryKey(query: LearningMetricsQuery, nonce: number): string {
  return `${query.since}|${query.until}|${query.businessId ?? ''}|${nonce}`;
}

/**
 * The Learning tab's numbers for one window and brand. `query` null means the
 * window is not one the endpoint accepts, so nothing is asked.
 *
 * Same shape as the other admin reads: a tagged result, an AbortController per
 * load, nothing written synchronously inside the effect. A new query shows
 * Loading rather than the previous window's numbers under the new dates.
 */
export function useLearningMetrics(query: LearningMetricsQuery | null): LearningMetricsResult {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [nonce, setNonce] = useState(0);

  const since = query?.since ?? null;
  const until = query?.until ?? null;
  const businessId = query?.businessId ?? null;

  useEffect(() => {
    if (since === null || until === null) return;
    const current = { since, until, businessId };
    const key = queryKey(current, nonce);
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const metrics = await fetchLearningMetrics(current, controller.signal);
        if (cancelled) return;
        setLoaded({ key, metrics, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          key,
          metrics: null,
          error: getErrorMessage(caught, 'Could not load the learning numbers.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [since, until, businessId, nonce]);

  const retry = useCallback(() => setNonce((value) => value + 1), []);

  if (query === null) return { metrics: null, isLoading: false, error: null, retry };

  const isCurrent = loaded !== null && loaded.key === queryKey(query, nonce);
  return {
    metrics: isCurrent ? loaded.metrics : null,
    isLoading: !isCurrent,
    error: isCurrent ? loaded.error : null,
    retry,
  };
}
